// The one place that turns a set of chart options into a rendered chart.
//
// Which constructor an options object needs is carried by the repo's own top-level `type` key, not
// by anything Highcharts defines: `chart.type` names a series shape (line, column, ...), while this
// names the whole chart's family. The distinction matters because the constructors are not
// interchangeable - `Highcharts.chart()` renders a stock chart's series perfectly well and silently
// builds none of the navigator or range chrome its options declare, so a chart drawn with the wrong
// one looks plausible and is missing half of what it was configured for.

import { board } from '@highcharts/dashboards/dashboards';
import { chart, stockChart, type Chart, type Options } from 'highcharts';
import { ChartType } from '@/types';

// Options as this app writes them: Highcharts' own, plus the family marker above.
export type ChartOptions = Options & { type?: ChartType };

// A Dashboards board is not a Highcharts chart and shares no interface with one, so a caller that
// can render both has to hold the union. Callers that only ever draw charts narrow it themselves.
// `board` is overloaded - it hands back a promise when asked to build asynchronously - and only the
// synchronous form is used here, which is what unwrapping the return type down to the board itself
// states.
export type ChartInstance = Chart | Awaited<ReturnType<typeof board>>;

export function createChart(element: HTMLElement, options: ChartOptions): ChartInstance {
    if (options?.type === ChartType.Stock) {
        return stockChart(element, options);
    }

    if (options?.type === ChartType.Dashboard) {
        // Dashboards takes its own options shape, which this app's chart options are not typed as -
        // the board is configured from the same object by convention rather than by a shared type.
        return board(element, options as any);
    }

    return chart(element, options);
}
